// A sandboxed frame for HTML we did not write: uploaded HTML blobs and
// agent-authored deliverables. Shared by the Documents viewer and the agents
// inline pane so there is one place the sandbox is spelled.
import React, { useMemo } from "react";

// sandbox="" allows nothing: no script execution, no top navigation, no
// forms, no popups, no same-origin access. The inlined meta-CSP is
// belt-and-suspenders against any sandbox bypass — it pins network origins
// for images/styles/scripts/connect to nothing remote and disables any
// <base> override. Inline ``data:`` images and inline styles still render, so
// a memo with its charts embedded looks like the memo.
const CSP_META =
  '<meta http-equiv="Content-Security-Policy" ' +
  "content=\"default-src 'none'; script-src 'none'; " +
  "img-src data:; style-src 'unsafe-inline'; base-uri 'none';\">";

// The CSP meta goes where the parser will honor it: inside <head> when the
// document has one, else right after <html>, else after the doctype. Ahead of
// the doctype it would be a parse error that drops the whole document into
// quirks mode, and generated HTML commonly omits <head>.
const HEAD_TAG = /<head(?=[\s>])[^>]*>/i;
const HTML_TAG = /<html(?=[\s>])[^>]*>/i;
const DOCTYPE = /<!doctype[^>]*>/i;

export const injectCsp = (html: string): string => {
  for (const anchor of [HEAD_TAG, HTML_TAG, DOCTYPE]) {
    if (anchor.test(html)) {
      return html.replace(anchor, (match) => `${match}${CSP_META}`);
    }
  }
  return `${CSP_META}${html}`;
};

const HtmlIframeView = ({
  html,
  title = "HTML document preview",
}: {
  html: string;
  title?: string;
}) => {
  // Memoized on the text: the host re-renders on every pane drag and every
  // streamed frame, and re-wrapping a megabyte each time is wasted work. The
  // frame never reloads either way — an equal srcdoc string is a no-op.
  // The lookahead keeps `<header>` from passing as `<head>`; a CSP meta
  // landing in the body is ignored.
  const wrapped = useMemo(() => injectCsp(html), [html]);
  return (
    <iframe
      srcDoc={wrapped}
      sandbox=""
      referrerPolicy="no-referrer"
      title={title}
      className="w-full h-full border-0"
    />
  );
};

export default HtmlIframeView;
